import type { CreateTenantResponse, Locale } from '@t4s/api-client/model'
import { timeZoneSchema } from '@t4s/domain'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate } from '@tanstack/react-router'
import { type FormEvent, useId, useRef, useState } from 'react'
import { ApiFailure, fieldErrorsOf } from '../lib/api-error.ts'
import { messageFor } from '../lib/messages.ts'
import { useDocumentTitle } from '../shell/document-title.ts'
import { Notice } from '../shell/notice.tsx'
import { Card } from './card.tsx'
import { LocaleFields, TimeZoneField } from './configuration-fields.tsx'
import { createTenantWithSuperadmin, TENANTS_QUERY_KEY } from './gateway.ts'
import { HandoffPanel } from './handoff.tsx'
import { LOCALE_LABELS, LOCALES } from './locales.ts'
import { correctSlugInput, SLUG_COUNTER_THRESHOLD, slugFieldError, suggestSlug } from './slug.ts'
import { TenantAddress } from './tenant-address.tsx'
import { canonicalTimeZone } from './time-zone.ts'

/**
 * Creating a tenant — docs/ux/PLATFORM-ADMIN.md §8, §9 and §10.
 *
 * **It is a page, not a dialog** (§8.1): six fields, a validation rule with a paragraph of
 * reasoning behind it, and an ending that holds a secret. A dialog would put all of that in
 * a box a stray Escape or a scrim press can close.
 *
 * **It is one call** (§8.4). Three calls from a browser have two partial failures and two
 * of them produce a company that exists and cannot be entered, so the API creates the
 * tenant, its first superadmin and that superadmin's activation link together. What this
 * screen owes that call is the four outcomes:
 *
 * | Outcome | What happens here |
 * |---|---|
 * | Everything succeeded | The form is replaced **in place** by the hand-off panel (§10) |
 * | The tenant was not created | The form stays with every value, and a notice names the reason from its code |
 * | The tenant was created and the superadmin was not | The owner lands on that tenant's page with a warning: the tenant exists, nobody can sign in, and that page has the control that fixes it. **Not a generic failure**, because the retry is different — creating the tenant again would fail on a slug that can never be reused |
 * | The write committed and the response never arrived | The form stays with every value and `client_timeout`'s sentence, which says the thing that matters: the request was sent, nothing came back, **so it may have worked**. Two controls — retry, and look this slug up in the list |
 *
 * The fourth row is the one an implementation gets wrong, because the server reports
 * nothing and the screen has to be right anyway. It is why `slug_taken`'s copy here does
 * **not** say "choose another": the holder may be the tenant this owner created seconds ago
 * and never saw the response for, and sending them to a different slug in that state is the
 * one unrecoverable mistake this screen can cause (§20.4).
 *
 * **Creation is not confirmed.** It is constructive, and confirming constructive actions is
 * how people learn to dismiss confirmations without reading them (§8.3).
 *
 * **One validation vocabulary** (TAS-17, C1). The browser's own bubble is off (`noValidate`), and the
 * submit button is *not* held inert while the form is incomplete — an inert button says nothing about
 * why. Pressing it (or Enter) with a field missing shows the same inline sentence the slug and the
 * timezone already showed, under every field that needs one, and puts focus in the first of them. The
 * sentences are live from then on: each goes the moment its field is valid.
 */

/**
 * The fields this form's own `validation_failed` can name — `chore-platform-field-error-
 * mapping`. `POST /platform/tenants`'s request body (`routes/tenants.ts`,
 * `CreateTenantRequestSchema`) nests the first administrator's two fields under
 * `superadmin`, which is why those two keys are the nested path in full rather than a bare
 * field name — see `fieldKeyOf`'s own doc comment (`lib/api-error.ts`) for why that path is
 * kept whole instead of collapsed to `superadmin`.
 */
type Field =
  | 'name'
  | 'slug'
  | 'timeZone'
  | 'defaultLocale'
  | 'enabledLocales'
  | 'superadminNickname'
  | 'superadminFullName'

const FIELD_FOR_PATH: Readonly<Record<string, Field>> = {
  name: 'name',
  slug: 'slug',
  timeZone: 'timeZone',
  defaultLocale: 'defaultLocale',
  enabledLocales: 'enabledLocales',
  'superadmin.nickname': 'superadminNickname',
  'superadmin.fullName': 'superadminFullName',
}

/**
 * One line per field, never the server's own prose (§20.1). Most of these are defensive —
 * `docs/DECISIONS.md`'s "A service-level field error's `details[].path`…" traces every
 * service-level check this form's fields could trip and finds each one already shadowed by
 * this form's own `complete` gate below (a blank-after-trim name, nickname or full name
 * disables Submit; the slug field mirrors the server's own domain rule; the locale pair
 * cannot be taken out of sync through this UI) or by the slug field's own client-side
 * check — but `name`'s 200-character cap, `timeZone`'s IANA-format check, and
 * `superadmin.nickname`/`superadmin.fullName`'s 120-/200-character caps have no client-side
 * mirror at all and are genuinely reachable here (the Timezone field is free text with only
 * suggestions, and none of Name, Nickname or Full name has a `maxLength`), which is what
 * this form is really wired for.
 */
function copyForField(field: Field): string {
  switch (field) {
    case 'name':
      return 'Enter a company name — at most 200 characters.'
    case 'slug':
      return 'Enter a valid slug.'
    case 'timeZone':
      return 'Enter a valid IANA time zone, such as Europe/Berlin.'
    case 'defaultLocale':
      return 'Choose a default language that is one of the enabled ones.'
    case 'enabledLocales':
      return 'Enable at least one language, including the default.'
    case 'superadminNickname':
      return 'Enter a nickname — at most 120 characters.'
    case 'superadminFullName':
      return 'Enter a full name — at most 200 characters.'
  }
}

export function CreateTenantScreen() {
  useDocumentTitle('Create a tenant', 'Tenants')
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const nameId = useId()
  const nameHelpId = useId()
  const slugId = useId()
  const slugErrorId = useId()
  const nicknameId = useId()
  const nicknameErrorId = useId()
  const fullNameId = useId()
  const fullNameErrorId = useId()
  const slugCounterId = useId()
  const nameField = useRef<HTMLInputElement>(null)
  const slugField = useRef<HTMLInputElement>(null)
  const zoneField = useRef<HTMLInputElement>(null)
  const fullNameField = useRef<HTMLInputElement>(null)
  const nicknameField = useRef<HTMLInputElement>(null)

  const [name, setName] = useState('')
  const [slug, setSlug] = useState('')
  /** The suggestion stops the moment the owner types in the slug field (§8.2). */
  const [slugTouched, setSlugTouched] = useState(false)
  const [timeZone, setTimeZone] = useState('Europe/Berlin')
  const [defaultLocale, setDefaultLocale] = useState<Locale>('de')
  const [enabledLocales, setEnabledLocales] = useState<Locale[]>(['de'])
  const [nickname, setNickname] = useState('')
  const [fullName, setFullName] = useState('')
  const [failureCode, setFailureCode] = useState<string | undefined>(undefined)
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<Field, string>>>({})
  const [handoff, setHandoff] = useState<CreateTenantResponse | undefined>(undefined)
  /**
   * Whether the owner has pressed Create with something missing. Until then a field is not accused
   * of being empty before it has been touched; from then on every missing field says so, live.
   */
  const [attempted, setAttempted] = useState(false)

  // A blank slug is only reported once the owner has typed in the field or pressed Create.
  const slugError = slugFieldError(slug, slugTouched || attempted)

  const create = useMutation({
    mutationFn: createTenantWithSuperadmin,
    onSuccess: (created) => {
      // The response is read **once into component state** and written to no cache
      // (§10.4): a query cache holding an activation link is a credential at rest.
      setHandoff(created)
      void queryClient.invalidateQueries({ queryKey: TENANTS_QUERY_KEY })
    },
    onError: (error) => {
      const failure = error instanceof ApiFailure ? error : undefined

      // `chore-platform-field-error-mapping`: a `validation_failed` naming a field this
      // form recognises marks that field and shows no generic notice — the generic
      // fallback is for a code this screen does not resolve at all, or for a
      // `validation_failed` whose `details[]` names nothing this form recognises, not for
      // both at once (mirrors `apps/app`'s own sheets, e.g. `location-sheet.tsx`).
      if (failure?.code === 'validation_failed') {
        const mapped = fieldErrorsOf<Field>(failure, FIELD_FOR_PATH, copyForField)
        if (Object.keys(mapped).length > 0) {
          setFieldErrors(mapped)
          setFailureCode(undefined)
          return
        }
      }

      setFieldErrors({})
      setFailureCode(failure?.code ?? 'client_unknown')

      // §8.4's third row. The tenant exists and its administrator does not, so the owner
      // belongs at that tenant rather than on a form whose retry would be refused — and
      // the response carries no id, so the list filtered to this exact slug is the way
      // there. `rescue` is what makes the list say why they arrived.
      if (failure?.code === 'tenant_without_superadmin') {
        void navigate({ to: '/tenants', search: { q: slug, state: 'any', rescue: true } })
      }
    },
  })

  // §8.2: the timezone is "a searchable list … validated against `timeZoneSchema` before submit".
  // The datalist is the search; this is the validation, so a value the server would refuse never
  // leaves the form. (A custom combobox would replace a native control, which §21 forbids.)
  const timeZoneError = timeZoneSchema.safeParse(timeZone).success
    ? undefined
    : copyForField('timeZone')

  // What is missing or refused, in the order the form reads — the first is where focus goes when
  // Create is pressed with something wrong (TAS-17). Each is judged from its value alone, not from
  // whether it has been accused yet.
  const invalidFields = [
    [name.trim() === '', nameField],
    [slugFieldError(slug, true) !== undefined, slugField],
    [timeZoneError !== undefined, zoneField],
    [fullName.trim() === '', fullNameField],
    [nickname.trim() === '', nicknameField],
  ] as const
  const complete =
    invalidFields.every(([invalid]) => !invalid) && enabledLocales.includes(defaultLocale)

  const nameError = attempted && name.trim() === '' ? copyForField('name') : undefined
  const fullNameError =
    attempted && fullName.trim() === '' ? copyForField('superadminFullName') : undefined
  const nicknameError =
    attempted && nickname.trim() === '' ? copyForField('superadminNickname') : undefined

  const submit = (event: FormEvent) => {
    event.preventDefault()
    setFailureCode(undefined)
    setFieldErrors({})
    if (!complete) {
      // Say so where the owner is looking, and put them in the first field that needs it, in the
      // order the form reads. Nothing is sent: the server would refuse it.
      setAttempted(true)
      invalidFields.find(([invalid]) => invalid)?.[1].current?.focus()
      return
    }
    create.mutate({
      name: name.trim(),
      slug,
      // In the browser's casing (`europe/kyiv` → `Europe/Kyiv`): the API stores what it is sent.
      timeZone: canonicalTimeZone(timeZone),
      defaultLocale,
      enabledLocales,
      superadmin: { nickname: nickname.trim(), fullName: fullName.trim() },
    })
  }

  if (handoff !== undefined) {
    return (
      <main className="page">
        <div className="page-header">
          <div>
            <p className="row-tight page-badge">
              <span className="badge badge-live">Created</span>
            </p>
            <h1>{handoff.tenant.name} is ready</h1>
            <p className="page-lead">
              One thing left: get the activation link to its first administrator.
            </p>
          </div>
        </div>
        <HandoffPanel
          // From the response rather than from the form: the server stores the nickname,
          // so this is the value the tenant page will show later, not a local echo.
          nickname={handoff.superadmin.nickname ?? nickname.trim()}
          fullName={handoff.superadmin.fullName}
          link={handoff.link}
          slug={handoff.tenant.slug}
          onDone={() => {
            setHandoff(undefined)
            void navigate({
              to: '/tenants/$tenantId',
              params: { tenantId: handoff.tenant.tenantId },
            })
          }}
        />
      </main>
    )
  }

  // In the list's own order, not the order they were ticked in, so the sentence reads the same
  // however the owner got there.
  const otherLocales = LOCALES.filter(
    (locale) => locale !== defaultLocale && enabledLocales.includes(locale),
  ).map((locale) => LOCALE_LABELS[locale])

  return (
    <main className="page">
      <div>
        <Link
          to="/tenants"
          className="crumb"
          aria-label="Back to Tenants"
          activeOptions={{ exact: true }}
        >
          <span aria-hidden="true">←</span> Tenants
        </Link>
        <div className="page-header">
          <div>
            <h1>Create a tenant</h1>
            <p className="page-lead">
              One step creates the company, its languages and its first administrator, who receives
              an activation link.
            </p>
          </div>
        </div>
      </div>

      {failureCode === undefined ? null : (
        <>
          <Notice tone="failure">{messageFor('createTenant', failureCode)}</Notice>
          {failureCode === 'slug_taken' || failureCode === 'client_timeout' ? (
            <button
              type="button"
              className="button"
              onClick={() => {
                // The lookup rather than "choose another": the owner sees for themselves
                // which of the three cases they are in (§9.3, §20.4).
                void navigate({ to: '/tenants', search: { q: slug, state: 'any' } })
              }}
            >
              Look for “{slug}” in the tenant list
            </button>
          ) : null}
        </>
      )}

      <form className="create-form" onSubmit={submit} noValidate>
        <div className="create-main">
          <Card title="The company">
            <div className="card-body form">
              <div className="field">
                <label htmlFor={nameId}>Company name</label>
                <input
                  id={nameId}
                  ref={nameField}
                  value={name}
                  required
                  aria-invalid={fieldErrors.name !== undefined || nameError !== undefined}
                  aria-describedby={nameHelpId}
                  onChange={(event) => {
                    setName(event.target.value)
                    if (!slugTouched) {
                      setSlug(suggestSlug(event.target.value))
                    }
                  }}
                />
                <div id={nameHelpId}>
                  {fieldErrors.name === undefined && nameError === undefined ? null : (
                    <p className="field-error">{fieldErrors.name ?? nameError}</p>
                  )}
                  <p className="muted">
                    As people say it. Two companies may genuinely share a name.
                  </p>
                </div>
              </div>

              <div className="field">
                <div className="row-between">
                  <label htmlFor={slugId}>Slug</label>
                  {slug.length >= SLUG_COUNTER_THRESHOLD ? (
                    <span className="tiny muted num" id={slugCounterId}>
                      {slug.length} of 63 characters
                    </span>
                  ) : null}
                </div>
                <input
                  id={slugId}
                  ref={slugField}
                  className="mono"
                  value={slug}
                  required
                  aria-invalid={slugError !== undefined || fieldErrors.slug !== undefined}
                  aria-describedby={
                    slug.length >= SLUG_COUNTER_THRESHOLD
                      ? `${slugErrorId} ${slugCounterId}`
                      : slugErrorId
                  }
                  onChange={(event) => {
                    setSlugTouched(true)
                    setSlug(correctSlugInput(event.target.value))
                  }}
                />
                <div id={slugErrorId}>
                  {/* `slugError` is this field's own live, client-side check against
                      `@t4s/domain`'s rule (§9.2) and recomputes from `slug` on every render, so it
                      already covers everything the server's identical rule could ever refuse —
                      `fieldErrors.slug` is shown only as a fallback for the case that check
                      cannot see: a slug the server rejects for a reason this client's own copy of
                      the rule does not yet know. */}
                  {slugError === undefined && fieldErrors.slug === undefined ? null : (
                    <p className="field-error">{slugError ?? fieldErrors.slug}</p>
                  )}
                  {/* This estate's address, not both estates' — a tenant created from this
                      console is created only on the estate this console talks to, and the other
                      estate's `<slug>` is either nothing or somebody else. */}
                  <p className="muted">
                    This tenant’s app <TenantAddress slug={slug} tense="future" />.
                  </p>
                  {/* One line, the rule. What a slug costs afterwards — never changed, never
                      released — is said in the summary, where the owner reads it before Create. */}
                  <p className="muted">Lowercase letters, digits and hyphens.</p>
                </div>
              </div>

              <TimeZoneField
                value={timeZone}
                onChange={setTimeZone}
                error={timeZoneError ?? fieldErrors.timeZone}
                inputRef={zoneField}
              />
            </div>
          </Card>

          <Card title="Languages" description="What this tenant’s staff can choose between.">
            <div className="card-body form">
              <LocaleFields
                locales={LOCALES}
                labels={LOCALE_LABELS}
                defaultLocale={defaultLocale}
                enabledLocales={enabledLocales}
                onDefaultChange={setDefaultLocale}
                onEnabledChange={setEnabledLocales}
                defaultLocaleError={fieldErrors.defaultLocale}
                enabledLocalesError={fieldErrors.enabledLocales}
              />
            </div>
          </Card>

          <Card
            title="The first administrator"
            description="There is no password field here, ever. Creating the tenant mints this person an activation link, and they choose their own password when they open it."
          >
            <div className="card-body">
              <div className="grid-2">
                <div className="field">
                  <label htmlFor={fullNameId}>Full name</label>
                  <input
                    id={fullNameId}
                    ref={fullNameField}
                    value={fullName}
                    required
                    aria-invalid={
                      fieldErrors.superadminFullName !== undefined || fullNameError !== undefined
                    }
                    aria-describedby={fullNameErrorId}
                    onChange={(event) => {
                      setFullName(event.target.value)
                    }}
                  />
                  {fieldErrors.superadminFullName === undefined &&
                  fullNameError === undefined ? null : (
                    <p className="field-error" id={fullNameErrorId}>
                      {fieldErrors.superadminFullName ?? fullNameError}
                    </p>
                  )}
                </div>

                <div className="field">
                  <label htmlFor={nicknameId}>Nickname</label>
                  <input
                    id={nicknameId}
                    ref={nicknameField}
                    value={nickname}
                    required
                    aria-invalid={
                      fieldErrors.superadminNickname !== undefined || nicknameError !== undefined
                    }
                    aria-describedby={nicknameErrorId}
                    onChange={(event) => {
                      setNickname(event.target.value)
                    }}
                  />
                  {fieldErrors.superadminNickname === undefined &&
                  nicknameError === undefined ? null : (
                    <p className="field-error" id={nicknameErrorId}>
                      {fieldErrors.superadminNickname ?? nicknameError}
                    </p>
                  )}
                  <p className="field-help">What they sign in with.</p>
                </div>
              </div>
            </div>
          </Card>
        </div>

        {/* §8.3: a request with six inputs deserves one restatement before it becomes
            irreversible. Not a confirmation dialog — a summary the owner reads. */}
        <div className="create-aside">
          <section className="summary" aria-label="What will be created">
            <h2>Before you submit</h2>
            <ul>
              <li>The company {name.trim() === '' ? '(unnamed)' : name.trim()}.</li>
              <li>
                Its app{' '}
                {slugFieldError(slug, true) === undefined ? (
                  <TenantAddress slug={slug} tense="future" />
                ) : (
                  '(choose a slug)'
                )}
                . The slug can never be changed and is never released, not even when the tenant is
                retired.
              </li>
              <li>
                Its business day computed in{' '}
                {timeZoneError === undefined ? canonicalTimeZone(timeZone) : '(choose a timezone)'}.
              </li>
              <li>
                Its languages: {LOCALE_LABELS[defaultLocale]} by default
                {otherLocales.length > 0 ? `, plus ${joinNames(otherLocales)}` : ''}.
              </li>
              <li>
                Its first administrator {fullName.trim() === '' ? '(unnamed)' : fullName.trim()},
                signing in as {nickname.trim() === '' ? '(no nickname)' : nickname.trim()}.
              </li>
            </ul>
            <button
              type="submit"
              className="button button-primary button-block"
              disabled={create.isPending}
            >
              {create.isPending ? 'Creating…' : 'Create the tenant and its first administrator'}
            </button>
            <p className="tiny muted">
              No confirmation follows — creating is constructive. The activation link appears once,
              on the next screen.
            </p>
          </section>
        </div>
      </form>
    </main>
  )
}

/** "English", "English and Русский", "English, Русский and Українська". */
function joinNames(names: readonly string[]): string {
  if (names.length <= 1) {
    return names.join('')
  }
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
}
